import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useChatStatusQuery } from '../api/chat/getChatStatus';
import useAnalytics, { TrackProps } from '../hooks/useAnalytics';

export type ChatContextType = {
  chatMaintenance?: boolean;
  showMaintenanceModal: boolean;
  setShowMaintenanceModal: React.Dispatch<React.SetStateAction<boolean>>;
  startChat: (
    track?: (eventName: string, properties: TrackProps) => void
  ) => void;
};

export const ChatContext = createContext<ChatContextType>({
  chatMaintenance: false,
  showMaintenanceModal: false,
  setShowMaintenanceModal: () => {},
  startChat: () => {},
});

export const ChatProvider = ({
  children,
  enabled,
}: React.PropsWithChildren<{ enabled: boolean }>): React.ReactElement => {
  const { track } = useAnalytics();

  const { data: chatStatus } = useChatStatusQuery(enabled);
  const [showMaintenanceModal, setShowMaintenanceModal] =
    useState<boolean>(false);

  const chatMaintenance = chatStatus?.chatMaintenance;

  useEffect(() => {
    if (chatMaintenance) {
      track('Response Received', {
        event_type: 'Automated System Process',
        flow: 'chat',
        status: 'disabled',
        reason: 'Maintenance Mode',
        action: 'Chat Status',
      });
    } else if (chatMaintenance === false) {
      track('Response Received', {
        event_type: 'Automated System Process',
        status: 'enabled',
        action: 'Chat Status',
      });
    }
  }, [chatMaintenance]);

  const startChat = useCallback(
    (track?: (eventName: string, properties: TrackProps) => void) => {
      if (chatMaintenance) {
        setShowMaintenanceModal(true);
      } else {
        const chatButton = document.querySelector<HTMLButtonElement>(
          "[data-selector='GUIDE_CHANNEL_BUTTON']"
        );
        if (window.cxone && chatButton) {
          chatButton.click();
          if (track) {
            track('Contact Accessed', {
              event_type: 'Link Accessed',
              contents: 'Start a conversation',
              link: 'Chat',
              location: 'Feature Card',
              method: 'Chat',
            });
          }
        }
      }
    },
    [chatMaintenance]
  );

  return (
    <ChatContext.Provider
      value={{
        chatMaintenance,
        showMaintenanceModal,
        setShowMaintenanceModal,
        startChat,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChatContext = () => useContext(ChatContext);
